import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { ApiError, ERROR_CODES } from '@/services/apiError'
import { ENTITY_TYPE_FOR_TIER } from '@/features/hierarchy/api'
import { MAX_PAGE_SIZE, fetchSensors } from '@/features/sensors/api'
import { readingInstant, readingMicros } from '@/features/anomalies/api'

/**
 * Saved sensor charts. LIVE.
 *
 * Contract: `forgeai-backend/app/api/sensors/chart.py` and
 * `app/schemas/sensors/chart.py`. A chart is a NAME and a set of sensor TAG ids,
 * attached to exactly one plant, line or machine, shared account-wide.
 *
 * ── Not `features/signals` ───────────────────────────────────────────────
 * That module is the demo Workstation's: channels with authored bands, states
 * and statistics, keyed by strings like `FDY-BM-03:VIBX`. Nothing here may use
 * those ids — the API takes `sensor_tags.id` UUIDs and nothing else. The ad-hoc
 * Workstation still runs on fixtures; saved charts run here.
 *
 * ── What the server enforces, mirrored only to avoid offering a refusal ──
 *  - `name`: 1–150 characters, required on create AND update (every write is a
 *    full replace).
 *  - At most `sensor_chart_max_tags_per_chart` (20) distinct tags. A bound the
 *    server REJECTS deviation from, so mirroring it is safe: drift fails loudly.
 *  - Every tag's sensor must sit AT the chart's place or beneath it
 *    (`verify_linked_sensor_tags(..., beneath=True)`). So "add signals" browses
 *    the chart's own subtree and nothing wider.
 *  - The place is create-only. A chart never moves.
 *
 * The API accepts a chart with no tags at all. The create screen asks for one
 * anyway — a chart that plots nothing is not a chart anybody meant to save — and
 * the detail screen lets the last one go, because that is a state the server
 * already allows and refusing it would leave a tag nobody can remove.
 */

export const NAME_MAX_LENGTH = 150
export const MAX_TAGS_PER_CHART = 20

/** The timeframe pills — the same three Live offers, each a rolling window
    ending now. Earlier spans are reached with the chart's Earlier / Later
    arrows, which slide the window by half its length. */
export const CHART_WINDOWS = ['1m', '5m', '15m']
export const DEFAULT_WINDOW = '15m'
const WINDOW_HOURS = { '1m': 1 / 60, '5m': 5 / 60, '15m': 0.25 }

/**
 * How much recent history the LIVE view keeps on screen. Short on purpose: the
 * stream replays only its last 60s on connect, so anything longer fills in from
 * the moment the view opened — the History view is for looking further back.
 */
export const LIVE_WINDOWS = ['1m', '5m', '15m']
export const DEFAULT_LIVE_WINDOW = '5m'
export const LIVE_WINDOW_MS = { '1m': 60_000, '5m': 300_000, '15m': 900_000 }

/** Hierarchy tier → the body key a chart attaches by. */
const PARENT_FIELD = { site: 'plant_id', line: 'line_id', asset: 'machine_id' }

/** Tiers a chart may attach to. The account root is not one. */
export const CHART_TIERS = new Set(Object.keys(PARENT_FIELD))

/**
 * Wire → app.
 *
 * `hierarchy` is the server's own `{plant, line, machine}` names, holes and all
 * — exactly what `HierarchyTrail` draws. `place` is the one id that is set, in
 * the hierarchy's vocabulary, so the browser can be pointed at the chart's
 * subtree without a second lookup.
 *
 * A tag's `name` is nullable on the sensor, so it is nullable here; the screens
 * fall back to the description and then to a translated "Unnamed tag", never to
 * an invented label.
 */
const toChart = (row) => ({
  id: row.id,
  name: row.name,
  plantId: row.plant_id ?? null,
  lineId: row.line_id ?? null,
  machineId: row.machine_id ?? null,
  place: row.machine_id
    ? { tier: 'asset', id: row.machine_id, name: row.hierarchy?.machine ?? null }
    : row.line_id
      ? { tier: 'line', id: row.line_id, name: row.hierarchy?.line ?? null }
      : row.plant_id
        ? { tier: 'site', id: row.plant_id, name: row.hierarchy?.plant ?? null }
        : null,
  hierarchy: {
    plant: row.hierarchy?.plant ?? null,
    line: row.hierarchy?.line ?? null,
    machine: row.hierarchy?.machine ?? null,
  },
  createdAt: row.created_at ?? null,
  sensorTags: (row.sensor_tags ?? []).map((tag) => ({
    id: tag.sensor_tag_id,
    name: tag.name ?? '',
    description: tag.description ?? '',
  })),
})

/** Sort fields the list accepts — `Literal["name", "created_at"]` server-side. */
export const CHART_SORTS = ['created_at', 'name']

/**
 * One page of the catalogue, with every filter `list_sensor_charts` takes.
 *
 *  - `search` — the server's `name ILIKE`, not a client filter over one page.
 *  - `place` — a plant, line or machine, and everything beneath it, sent as
 *    `asset_type` + `asset_id` (the server applies them only as a pair). So
 *    "Line 1" also finds the charts on its machines.
 *
 *    The list also takes `plant_id` / `line_id` / `machine_id`, and they are
 *    NOT an "attached exactly here" filter: `apply_asset_ids` compares them with
 *    the chart's RESOLVED path (`hierarchy_joins` walks a machine chart up to
 *    its line and plant), so `line_id` returns the same subtree. Offering them
 *    as a second control would be a toggle that changes nothing.
 *  - `sortBy` / `sortDir` — newest first unless asked otherwise: a chart
 *    somebody just made is the one they are looking for.
 */
export async function fetchSensorCharts(
  { search, page = 1, pageSize = 50, sortBy = 'created_at', sortDir = 'desc', place = null } = {},
  signal,
) {
  const where = place ? { asset_type: ENTITY_TYPE_FOR_TIER[place.tier], asset_id: place.id } : {}
  const data = await get(endpoints.sensorCharts.list, {
    signal,
    params: {
      sort_by: CHART_SORTS.includes(sortBy) ? sortBy : 'created_at',
      sort_dir: sortDir === 'asc' ? 'asc' : 'desc',
      page,
      page_size: pageSize,
      ...where,
      ...(search?.trim() ? { search: search.trim() } : {}),
    },
  })
  return {
    items: (data?.sensor_charts ?? []).map(toChart),
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
  }
}

/**
 * One chart.
 *
 * ── There is no `GET /sensors/charts/{id}` ───────────────────────────────
 * So the chart is found by walking the list at the largest page the API
 * accepts. On an account with a few hundred charts that is one or two requests;
 * the list is scoped by the same grant the detail would be, so a chart outside
 * the reader's scope is correctly "not found" rather than leaked. Recorded in
 * BACKEND-GAPS.md — when the route lands this function becomes one `get`.
 *
 * Not found is thrown as a real 404 `ApiError`, so the screen's error path is the
 * one a missing record would take from a detail route.
 */
export async function fetchSensorChart(chartId, signal) {
  for (let page = 1, pages = 1; page <= pages; page += 1) {
    const answer = await fetchSensorCharts({ page, pageSize: MAX_PAGE_SIZE }, signal)
    const found = answer.items.find((chart) => chart.id === chartId)
    if (found) return found
    pages = answer.totalPages || 1
  }
  throw new ApiError({ code: ERROR_CODES.NOT_FOUND, status: 404, detail: 'Sensor chart not found.' })
}

/**
 * Create.
 *
 * All three parent keys are SENT, two of them null: `ExactlyOneParentMixin`
 * declares `X | None` with no default, so an omitted key is a 422 rather than a
 * null — the standing rule in this API.
 */
export async function createSensorChart({ name, place, sensorTagIds }) {
  const body = {
    name: name.trim(),
    plant_id: null,
    line_id: null,
    machine_id: null,
    [PARENT_FIELD[place.tier]]: place.id,
    sensor_tag_ids: [...new Set(sensorTagIds)],
  }
  return toChart(await post(endpoints.sensorCharts.list, body))
}

/**
 * Update — name and tags, a FULL replace of both.
 *
 * Adding one tag is therefore "the current set plus one", and removing one is
 * "the current set minus one", with the name sent back unchanged. The server
 * syncs the set as a delta, so tags that stay keep their rows.
 */
export async function updateSensorChart({ id, name, sensorTagIds }) {
  return toChart(
    await put(endpoints.sensorCharts.detail(id), {
      name: name.trim(),
      sensor_tag_ids: [...new Set(sensorTagIds)],
    }),
  )
}

export async function deleteSensorChart(id) {
  await del(endpoints.sensorCharts.detail(id))
  return id
}

/* ══ PLACES ══════════════════════════════════════════════════════════════ */

/** The `asset_type`/`asset_id` pair that names a place's whole subtree. */
export const subtreeOf = (place) =>
  place ? { assetType: ENTITY_TYPE_FOR_TIER[place.tier], assetId: place.id } : null

/**
 * Every sensor at a place or beneath it, all pages.
 *
 * `asset_type`/`asset_id` is the server's subtree filter (`asset_scope_filter`):
 * a line answers its own sensors AND its machines'. That is the same rule the
 * chart write checks with, so what this lists is exactly what may be added.
 *
 * Note the permission split: this list is behind `sensors.manage_sensors` or
 * `sensors.manage_connections`, while charts are behind `sensor_explorer.*`.
 * See `useSensorChartAccess`.
 */
export async function fetchLocationSensors(place, signal) {
  const scope = subtreeOf(place)
  const out = []
  const seen = new Set()
  for (let page = 1, pages = 1; page <= pages; page += 1) {
    const answer = await fetchSensors({ ...scope, page, pageSize: MAX_PAGE_SIZE }, signal)
    // De-duplicated by id: keyset-less offset paging can repeat a row across a
    // page boundary if one is inserted mid-walk, and a tree must not show it twice.
    for (const sensor of answer.items) {
      if (!seen.has(sensor.id)) {
        seen.add(sensor.id)
        out.push(sensor)
      }
    }
    pages = answer.totalPages || 1
  }
  return out
}

/* ══ READINGS ════════════════════════════════════════════════════════════ */

/**
 * A naive-UTC `YYYY-MM-DDTHH:MM:SS` — what `NaiveUTCDatetime` accepts. It
 * REJECTS an aware value, so the `Z` must not be sent.
 */
const toNaiveUtc = (ms) => new Date(ms).toISOString().slice(0, 19)

/**
 * The span a chart is read over: a rolling window ending now, or a closed range
 * from the calendar. Returns instants in ms, plus the naive strings the API takes.
 */
export function resolveSpan({ window, from, to }, now = Date.now()) {
  const start = from ? Date.parse(from) : NaN
  const end = to ? Date.parse(to) : NaN
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    return { fromMs: start, toMs: end, from: toNaiveUtc(start), to: toNaiveUtc(end), closed: true }
  }
  const hours = WINDOW_HOURS[window] ?? WINDOW_HOURS[DEFAULT_WINDOW]
  const stop = Math.floor(now / 1000) * 1000
  const begin = stop - hours * 3_600_000
  return { fromMs: begin, toMs: stop, from: toNaiveUtc(begin), to: toNaiveUtc(stop), closed: false }
}

/**
 * ONE page of every tag's readings in `[from, to)`, oldest first.
 *
 * ── How the chart endpoint pages ─────────────────────────────────────────
 * It reads up to a page of rows PER TAG, and if any tag fills its page it cuts
 * `to` back to just past that tag's last row and drops every other tag's rows
 * beyond it. So all tags advance together, and "is there more" is simply "did
 * `to` come back earlier than asked". The next page is `from = that to`, with
 * the same end. No page-size constant enters into it.
 *
 * Non-numeric values (an `enum` or `string` tag) are not plottable and are not
 * drawn as zero — they are dropped, as the anomaly traces drop them.
 *
 * @returns {Promise<{readings: Array<{tagId: string, at: string, value: number}>, next: string|null}>}
 */
export async function fetchSensorChartReadingPage({ chartId, from, to }, signal) {
  const data = await get(endpoints.sensorCharts.readings(chartId), {
    signal,
    params: { from, to },
  })

  const readings = []
  for (const row of Array.isArray(data?.readings) ? data.readings : []) {
    const value = Number(row?.value)
    const at = readingInstant(row?.timestamp)
    if (Number.isFinite(value) && at) readings.push({ tagId: row.sensor_tag_id, at, value })
  }

  const answeredTo = readingMicros(data?.to)
  const askedTo = readingMicros(to)
  const next =
    Number.isFinite(answeredTo) && Number.isFinite(askedTo) && answeredTo < askedTo && data.to !== from
      ? data.to
      : null

  return { readings, next }
}
